import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { type CrossClientQueryClientQueryMode, type CrossClientQueryDataSource } from '../../../models/CrossClient'
import { isCrossClientQueryClientQueryMode } from '../utils'

const CROSS_CLIENT_LANDING_PATH = '/cross-client'
const QUESTION_PARAM = 'question'
const SOURCES_PARAM = 'sources'
const CLIENTS_PARAM = 'clients'
const PREFILL_PARAMS = [QUESTION_PARAM, SOURCES_PARAM, CLIENTS_PARAM]
const SOURCES_SEPARATOR = ','

export interface CrossClientLandingPrefill {
  question?: string
  sources?: CrossClientQueryDataSource[]
  clientQueryMode?: CrossClientQueryClientQueryMode
}

const parseIntegrationSources = (value: string): CrossClientQueryDataSource[] => {
  const integrationTypes = value
    .split(SOURCES_SEPARATOR)
    .map((integrationType) => integrationType.trim())
    .filter((integrationType) => integrationType.length > 0)
  return [...new Set(integrationTypes)].map((integrationType) => ({ type: 'integration', integrationType }))
}

export const parseCrossClientLandingPrefill = (searchParams: URLSearchParams): CrossClientLandingPrefill => {
  const question = searchParams.get(QUESTION_PARAM)?.trim()
  const sources = searchParams.get(SOURCES_PARAM)
  const clientQueryMode = searchParams.get(CLIENTS_PARAM)
  return {
    ...(question ? { question } : {}),
    ...(sources === null ? {} : { sources: parseIntegrationSources(sources) }),
    ...(isCrossClientQueryClientQueryMode(clientQueryMode) ? { clientQueryMode } : {}),
  }
}

export const crossClientLandingPath = ({ question, sources, clientQueryMode }: CrossClientLandingPrefill): string => {
  const params = new URLSearchParams()
  if (question !== undefined) {
    params.set(QUESTION_PARAM, question)
  }
  if (sources !== undefined) {
    const integrationTypes = sources.flatMap((source) => (source.type === 'integration' ? [source.integrationType] : []))
    params.set(SOURCES_PARAM, integrationTypes.join(SOURCES_SEPARATOR))
  }
  if (clientQueryMode !== undefined) {
    params.set(CLIENTS_PARAM, clientQueryMode)
  }
  const search = params.toString()
  return search ? `${CROSS_CLIENT_LANDING_PATH}?${search}` : CROSS_CLIENT_LANDING_PATH
}

export const useCrossClientLandingPrefill = (): CrossClientLandingPrefill => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [prefill] = useState(() => parseCrossClientLandingPrefill(searchParams))
  const hasPrefillParams = PREFILL_PARAMS.some((param) => searchParams.has(param))

  useEffect(() => {
    if (!hasPrefillParams) {
      return
    }
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous)
        PREFILL_PARAMS.forEach((param) => next.delete(param))
        return next
      },
      { replace: true },
    )
  }, [hasPrefillParams, setSearchParams])

  return prefill
}
